import { useEffect, useMemo, useState } from 'react';
import { Plus, Edit3, Trash2, Save, Lock, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const ALL_PERMS = [
    { group: 'Admin', items: [
        { key: 'admin.dashboard',      label: 'Dashboard' },
        { key: 'admin.approvals',      label: 'Approvals queue' },
        { key: 'admin.users',          label: 'Users' },
        { key: 'admin.access',         label: 'Access & permissions' },
        { key: 'admin.roles',          label: 'Roles' },
        { key: 'admin.downloads',      label: 'Downloads (manage)' },
        { key: 'admin.incentives',     label: 'Incentives' },
        { key: 'admin.reimbursements', label: 'Reimbursements (review)' },
        { key: 'admin.achievers',      label: 'Banner Update' },
        { key: 'admin.hero-slides',    label: 'Hero Slides' },
        { key: 'admin.franchises',     label: 'Franchise manager' },
        { key: 'admin.form-builder',   label: 'Form builder' },
        { key: 'admin.clients',          label: 'All clients' },
        { key: 'admin.payment-ledger',   label: 'Payment ledger' },
        { key: 'admin.settings',       label: 'Settings' },
        { key: 'admin.kpi-analytics',  label: 'KPI analytics' },
        { key: 'admin.aoi-calculator', label: 'AOI calculator' },
        { key: 'admin.aoi-location',   label: 'AOI location' },
        { key: 'admin.refunds',        label: 'Refunds' },
        { key: 'admin.zoho-leads',     label: 'Zoho leads' },
        { key: 'admin.activity',       label: 'Activity log' },
    ]},
    { group: 'Sales / Workspace', items: [
        { key: 'sales.dashboard',      label: 'Dashboard' },
        { key: 'sales.kpi-analytics',  label: 'KPI analytics' },
        { key: 'sales.leads',          label: 'My leads' },
        { key: 'sales.clients',        label: 'My clients' },
        { key: 'sales.legal',          label: 'Legal' },
        { key: 'sales.clients.new',    label: 'Register client' },
        { key: 'sales.downloads',      label: 'Downloads (view)' },
        { key: 'sales.incentives',     label: 'My incentives' },
        { key: 'sales.reimbursements', label: 'My reimbursements' },
        { key: 'sales.team-leads',     label: 'Team leads' },
        { key: 'sales.my-team',        label: 'My Team' },
    ]},
    { group: 'Client actions', items: [
        { key: 'action.loi-stage.update', label: 'Update LOI stage' },
        { key: 'action.agreement-stage.update', label: 'Update Agreement stage' },
    ]},
];

const PROTECTED_SLUGS = new Set(['admin', 'sales_user']);

const blankRole = () => ({
    name: '', slug: '', description: '',
    permissions: [], color: '#00B09B', isActive: true, isSuperUser: false,
});

function roleSlug(r) {
    return String(r?.slug || '').toLowerCase().trim();
}

function canDeleteRole(r) {
    const slug = roleSlug(r);
    return !!slug && !PROTECTED_SLUGS.has(slug);
}

export default function Roles() {
    const [roles, setRoles] = useState([]);
    const [editing, setEditing] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const load = () => api.get('/roles').then(r => setRoles(r.data.items || []));
    useEffect(() => { load(); }, []);

    const remove = async (r) => {
        const slug = roleSlug(r);
        if (!slug) return toast.error('This role has no slug — cannot delete.');
        if (PROTECTED_SLUGS.has(slug)) {
            return toast.error(`"${r.name}" is protected and cannot be deleted.`);
        }
        if (!confirm(`Delete role "${r.name}" (${slug})?\n\nUsers on this role must be reassigned first. This cannot be undone.`)) return;
        setDeletingId(r._id);
        try {
            await api.delete(`/roles/${r._id}`);
            toast.success(`"${r.name}" deleted`);
            await load();
        } catch { /* toasted by api interceptor */ }
        finally { setDeletingId(null); }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold">Roles</h1>
                    <p className="text-sm text-ink-500">
                        Build custom roles and pick which pages each one sees.{' '}
                        <span className="text-ink-400">Only <code>admin</code> and <code>sales_user</code> are protected from deletion.</span>
                    </p>
                </div>
                <button className="ag-btn-primary" onClick={() => setEditing(blankRole())}>
                    <Plus className="h-4 w-4" /> New role
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {roles.map(r => (
                    <div key={r._id} className="ag-card p-5">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="h-3 w-3 rounded-full" style={{ background: r.color || '#00B09B' }} />
                                    <h3 className="font-heading font-bold text-lg truncate">{r.name}</h3>
                                    {r.isSystem && <Lock className="h-3.5 w-3.5 text-ink-500" title="System role" />}
                                    {r.isSuperUser && <ShieldCheck className="h-3.5 w-3.5 text-brand-teal-700" title="Super user" />}
                                </div>
                                <code className="text-[10px] font-mono text-ink-500">{r.slug}</code>
                                <p className="text-xs text-ink-700 mt-2 line-clamp-2">{r.description || '—'}</p>
                            </div>
                            <div className="flex gap-1 shrink-0">
                                <button type="button" className="ag-btn-ghost text-xs py-1 px-2" title="Edit role" onClick={() => setEditing(r)}>
                                    <Edit3 className="h-3.5 w-3.5" />
                                </button>
                                {canDeleteRole(r) ? (
                                    <button
                                        type="button"
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors disabled:opacity-60"
                                        title="Delete role"
                                        disabled={deletingId === r._id}
                                        onClick={() => remove(r)}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-ink-200 bg-ink-50 text-ink-400 hover:bg-ink-100 hover:text-ink-600 transition-colors"
                                        title={`"${r.name}" is protected — cannot delete`}
                                        onClick={() => toast.error(`"${r.name}" is protected and cannot be deleted.`)}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-1">
                            {(r.permissions || []).slice(0, 6).map(p => (
                                <span key={p} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-ink-100 text-ink-700">{p}</span>
                            ))}
                            {(r.permissions || []).length > 6 && (
                                <span className="text-[10px] text-ink-500">+ {(r.permissions || []).length - 6} more</span>
                            )}
                            {(!r.permissions || r.permissions.length === 0) && (
                                <span className="text-[10px] text-ink-500 italic">No pages — user will see only landing</span>
                            )}
                        </div>
                    </div>
                ))}
                {roles.length === 0 && <div className="col-span-full text-center text-ink-500 py-12">No roles yet — system roles seed on first server boot.</div>}
            </div>

            {editing && <RoleEditor role={editing} onClose={(s) => { setEditing(null); if (s) load(); }} />}
        </div>
    );
}

function RoleEditor({ role, onClose }) {
    const [form, setForm] = useState({ ...role, permissions: [...(role.permissions || [])] });
    const [busy, setBusy] = useState(false);

    const isNew = !form._id;
    const isSystem = !!form.isSystem;

    const togglePerm = (key) => setForm(f => ({
        ...f,
        permissions: f.permissions.includes(key) ? f.permissions.filter(x => x !== key) : [...f.permissions, key],
    }));
    const toggleGroup = (group) => {
        const keys = group.items.map(i => i.key);
        const allOn = keys.every(k => form.permissions.includes(k));
        setForm(f => ({ ...f, permissions: allOn ? f.permissions.filter(p => !keys.includes(p)) : [...new Set([...f.permissions, ...keys])] }));
    };

    const allCount = useMemo(() => ALL_PERMS.reduce((a, g) => a + g.items.length, 0), []);

    const submit = async (e) => {
        e?.preventDefault();
        if (!form.name) return toast.error('Name required');
        if (isNew && !form.slug) return toast.error('Slug required');
        setBusy(true);
        try {
            if (isNew) await api.post('/roles', form);
            else       await api.patch(`/roles/${form._id}`, form);
            toast.success('Saved');
            onClose(true);
        } catch {/* toasted */}
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-ink-900/40 grid place-items-center p-4" onClick={() => onClose(false)}>
            <form onSubmit={submit} className="bg-white rounded-xl2 shadow-soft w-full max-w-3xl p-6 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-heading font-bold text-xl">{isNew ? 'New role' : 'Edit role'}</h3>
                        {isSystem && <p className="text-xs text-amber-700 mt-1 flex items-center gap-1"><Lock className="h-3 w-3" /> System role — slug locked.</p>}
                        {!isNew && !canDeleteRole(form) && (
                            <p className="text-xs text-rose-700 mt-1">Protected role — cannot be deleted.</p>
                        )}
                    </div>
                    <button type="button" onClick={() => onClose(false)} className="text-ink-500 hover:text-ink-900 text-xl">✕</button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                    <div>
                        <label className="ag-label">Display name *</label>
                        <input className="ag-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Sub Admin" />
                    </div>
                    <div>
                        <label className="ag-label">Slug *</label>
                        <input className="ag-input font-mono text-xs" value={form.slug} disabled={!isNew}
                               onChange={e => setForm({...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_')})}
                               placeholder="e.g. sub_admin" />
                    </div>
                </div>
                <div className="mt-3">
                    <label className="ag-label">Description</label>
                    <input className="ag-input" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="One line about what this role can do" />
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                    <div>
                        <label className="ag-label">Color</label>
                        <input type="color" className="ag-input h-10 p-1" value={form.color} onChange={e => setForm({...form, color: e.target.value})} />
                    </div>
                    <label className="flex items-end gap-2 pb-2 cursor-pointer">
                        <input type="checkbox" checked={form.isActive} onChange={e => setForm({...form, isActive: e.target.checked})} />
                        <span className="text-sm">Active</span>
                    </label>
                    <label className="flex items-end gap-2 pb-2 cursor-pointer">
                        <input type="checkbox" checked={form.isSuperUser} disabled={isSystem} onChange={e => setForm({...form, isSuperUser: e.target.checked})} />
                        <span className="text-sm">Super user <span className="text-[11px] text-ink-500">(bypasses backend gates)</span></span>
                    </label>
                </div>

                {/* Permissions grid */}
                <div className="mt-6 border-t border-ink-100 pt-4">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="font-heading font-semibold">Page access</div>
                            <div className="text-xs text-ink-500">{form.permissions.length} of {allCount} pages selected</div>
                        </div>
                    </div>
                    <div className="space-y-4">
                        {ALL_PERMS.map(group => {
                            const allOn = group.items.every(i => form.permissions.includes(i.key));
                            return (
                                <div key={group.group}>
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-xs uppercase tracking-wider text-ink-500 font-bold">{group.group}</h4>
                                        <button type="button" className="text-xs ag-link" onClick={() => toggleGroup(group)}>
                                            {allOn ? 'Clear all' : 'Select all'}
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {group.items.map(item => {
                                            const on = form.permissions.includes(item.key);
                                            return (
                                                <button key={item.key} type="button" onClick={() => togglePerm(item.key)}
                                                    className={`flex items-center justify-between gap-2 p-2.5 rounded-lg border transition text-left
                                                    ${on ? 'border-brand-teal/40 bg-brand-teal-50' : 'border-ink-300 hover:border-ink-500/30'}`}>
                                                    <span className="flex items-center gap-2 text-sm">
                                                        {on ? <Eye className="h-4 w-4 text-brand-teal-700" /> : <EyeOff className="h-4 w-4 text-ink-500" />}
                                                        {item.label}
                                                    </span>
                                                    <code className="text-[10px] font-mono text-ink-500">{item.key}</code>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-ink-100 sticky bottom-0 bg-white">
                    <button type="button" className="ag-btn-ghost" onClick={() => onClose(false)}>Cancel</button>
                    <button className="ag-btn-primary" disabled={busy}><Save className="h-4 w-4" /> {busy ? 'Saving…' : 'Save role'}</button>
                </div>
            </form>
        </div>
    );
}
